import { BsDot } from 'react-icons/bs';
import { HiPrinter } from 'react-icons/hi';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';
import Badge from 'src/components/_common/Badge/Badge';
import Button from 'src/components/_common/Button/Button';
import { RoleTypes } from 'src/models/roles';
import SupervisorBadge from 'src/pages/Student/StudentForms/FormListPage/SupervisorBadge';
import ShowSigningFormPdf from 'src/pages/Student/StudentForms/ShowSigningFormPdf';
import ShowStudentPdf from 'src/pages/Student/StudentForms/ShowStudentPdf';
import ShowWarningPdf from 'src/pages/Student/StudentForms/ShowWarningPdf';
import ApiErrorAlert from '../_common/ApiErrorAlert/ApiErrorAlert';
import PageHeader from '../_common/PageHeader/PageHeader';
import Spinner from '../_common/Spinner/Spinner';
import FormsSection from './FormsSection';
import SigningFormSection from './SigningFormSection/SigningFormSection';
import AttendanceSection from './AttendanceSection/AttendanceSection';
import StudentWarningSection from './StudentWarningSection/StudentWarningSection';
import { usePreviousStudentPassWarningQuery } from './StudentWarningSection/queries';
import PreviousWarningSection from './PreviousWarningSection';
import { useStudentPageQuery } from './queries';
import useLoggedInUser from 'src/hooks/user/useLoggedInUser';
import { useEffect, useState } from 'react';
import { canAdvisorCreateSigningForm } from 'src/components/SigningForm/utils';
import { isAttendanceFeatureEnabled } from 'src/utils/featureFlags';

const StudentPage = ({ activeRole }: { activeRole: RoleTypes }) => {
  const { classId, userId } = useParams<{ classId: string; userId: string }>();
  
  const [isPrimaryTeacher, setIsPrimaryTeacher] = useState(false);

  const { data: loggedInUser } = useLoggedInUser();
  
  const query = useStudentPageQuery(+(classId ?? '0'), +(userId ?? '0'));
  const teacher = query.data?.teacher;
  const previousWarningQuery = usePreviousStudentPassWarningQuery(
    +(classId ?? '0'),
    +(userId ?? '0'),
    !!classId && !!userId && (activeRole === RoleTypes.Teacher || activeRole === RoleTypes.Advisor),
  );

  useEffect(() => {
    setIsPrimaryTeacher(activeRole === RoleTypes.Teacher && loggedInUser?.id === teacher?.id);
  }, [teacher, loggedInUser, activeRole]);

  const handlePdf = () => {
    if (classId && userId) {
      ShowStudentPdf(+classId, +userId);
    }
  };

  const handleSigningFormPdf = () => {
    if (classId && userId) {
      ShowSigningFormPdf(+classId, +userId);
    }
  };

  const handleWarningPdf = () => {
    if (classId && userId) {
      ShowWarningPdf(+classId, +userId);
    }
  };

  if (!classId || !userId) return <Spinner />;
  if (query.isLoading) return <Spinner />;
  if (query.isError) return <ApiErrorAlert error={query.error} message="" />;
  
  const {
    class: _class,
    student,
    warning,
    supervisor,
    signingForm,
  } = query.data;

  const sortedSupervisorIds = student.studentUserReferences
    ? [...student.studentUserReferences].sort((a, b) => {
        if (a.userId === supervisor?.id) return -1;
        if (b.userId === supervisor?.id) return 1;
        return 0;
      })
    : [];
  
  return (
    <>
      <PageHeader title={query.data.student.displayName}>
        <div className="flex justify-center items-center text-darkerGray">
          {query.data.class.subject?.subjectCode}
          <BsDot />
          {query.data.class.period?.name}
        </div>
        <div className="mt-5">
          <Button onClick={handlePdf} iconLeft={HiPrinter}>
            <FormattedMessage id="forms" />
          </Button>
          {signingForm && (
            <Button
              onClick={handleSigningFormPdf}
              iconLeft={HiPrinter}
              className="ml-2"
              data-testid="signingform-download-button"
            >
              <FormattedMessage id="signing-form-header" />
            </Button>
          )}
          {warning && (
            <Button
              onClick={handleWarningPdf}
              iconLeft={HiPrinter}
              className="ml-2"
              data-testid="warning-download-button"
            >
              <FormattedMessage id="warning" />
            </Button>
          )}
        </div>
      </PageHeader>
      {activeRole === RoleTypes.Teacher && (
        <>
          {(teacher || sortedSupervisorIds.length > 0) && (
            <div className="flex flex-wrap gap-2 justify-center">
              {teacher && (
                <Badge type="teacher">
                  {teacher.displayName} (<FormattedMessage id="teacher" />)
                </Badge>
              )}
              {sortedSupervisorIds.map(sup => (
                <SupervisorBadge
                  key={sup.userId}
                  id={sup.userId}
                  isSecondary={sup.userId !== supervisor?.id}
                />
              ))}
            </div>
          )}
        </>
      )}
      {previousWarningQuery.data && (
        <PreviousWarningSection student={student} warning={previousWarningQuery.data} />
      )}
      <StudentWarningSection
        class={_class}
        student={student}
        warning={warning}
        isPrimaryTeacher={isPrimaryTeacher}
        supervisor={supervisor}
        teacher={teacher}
      />
      <SigningFormSection
        signingForm={signingForm}
        showCreateButton={isPrimaryTeacher || (!!loggedInUser && canAdvisorCreateSigningForm(loggedInUser, student.studentReferences))}
      />
      {activeRole === RoleTypes.Advisor && isAttendanceFeatureEnabled() && (
        <AttendanceSection className={signingForm ? undefined : 'mt-8'} />
      )}
      <FormsSection class={_class} signingForm={signingForm!} />
    </>
  );
};

export default StudentPage;
